import { FileUploadComponent } from "@iventis/components";
import React, { useEffect, useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { styled } from "@iventis/styles";
import { fileToBase64, getImageDimensions, megaBytesToBytes, resizeFileImage, resizeImageGivenMaxWidthAndHeight } from "@iventis/utilities";

/** Size of image has to be after resize */
const MAX_FILE_SIZE_AFTER_RESIZE_MB = 3.8;

/** Max width of image */
const MAX_WIDTH_PX = 1920;

/** Max height of image */
const MAX_HEIGHT_PX = 1080;

export const PdfExportImageUploadComponent: React.FC<{
    imageUrl?: string;
    fileName?: string;
    disabled?: boolean;
    handleChange: (value: string) => void;
    maxFileSize: number;
}> = ({ imageUrl, fileName, disabled, handleChange, maxFileSize }) => {
    const [imgFileName, setImgFileName] = useState<string>();
    const [fileAdded, setFileAdded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>(undefined);
    const translate = useIventisTranslate();
    const validFileExtensions = ["jpg", "jpeg", "png"];

    useEffect(() => {
        setImgFileName(fileName);
    }, [fileName]);

    const tryUpload = async (file: File) => {
        setLoading(true);
        setImgFileName(file.name);
        setErrorMessage(undefined);
        let compressedFile = file;
        if (file.size > megaBytesToBytes(MAX_FILE_SIZE_AFTER_RESIZE_MB)) {
            let { height, width } = await getImageDimensions(file);
            const { height: newHeight, width: newWidth } = resizeImageGivenMaxWidthAndHeight(width, height, MAX_WIDTH_PX, MAX_HEIGHT_PX);
            height = newHeight;
            width = newWidth;
            compressedFile = await resizeFileImage(file, newWidth, newHeight);
            // If file is still too big after resizing
            if (compressedFile.size > megaBytesToBytes(MAX_FILE_SIZE_AFTER_RESIZE_MB)) {
                setErrorMessage(translate(Content.map9.fileUpload.invalid_compression_size));
                setImgFileName(null);
                tryRemove();
                setLoading(false);
                return;
            }
        }
        const { withHeader: image } = await fileToBase64(compressedFile);
        handleChange(image);
        setFileAdded(true);
        setLoading(false);
    };

    const tryRemove = async () => {
        setLoading(true);
        setImgFileName(undefined);
        setFileAdded(false);
        handleChange(null);
        setLoading(false);
    };

    return (
        <>
            <StyledFileUploadComponent
                PreviewComponent={() => null}
                removeRequested={tryRemove}
                previewImageClassName="pdf-export-image-preview-container"
                loading={loading}
                fileName={imgFileName}
                fileThumbnailUrl={imageUrl}
                uploadFile={tryUpload}
                uploadButtonText={!fileAdded ? translate(Content.common.buttons.upload) : translate(Content.common.buttons.delete)}
                inputAccept="image/*"
                persistFileSelectInput
                disabled={disabled}
                showDeleteButtonOnFileUploaded
                showText
                maxFileSize={maxFileSize}
                fileExtensions={validFileExtensions}
                externalErrorMessage={errorMessage}
                dataTestIdInput="export-custom-logo-input"
            />
        </>
    );
};

const StyledFileUploadComponent = styled(FileUploadComponent)`
    .file-upload-input {
        flex: 1;
        max-width: 200px;
        margin: 10px 0 5px;
    }

    .file-upload-button {
        margin: 10px 0 5px;
    }
`;
